import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/inputs/Button';
import CaseOpenLink from 'components/CaseOpenLink';
import Modal from 'components/Modal';
import Table from 'components/Table';
import { ColumnSetting } from 'components/Table/utils';
import useModalStack from 'contexts/modal/useModalStack';
import styled from 'styled-components';
import { AXIOS_INSTANCE } from 'api/mutator/custom-instance';
import {
  getGlobalSettingsGetSettingsQueryKey,
  useGlobalSettingsGetSettings,
  useGlobalSettingsUpdateSetting,
} from 'api/global-settings/global-settings';
import { GlobalSettingEnum, VehicleBrandRuleKind } from 'api/model';
import GlobalSettingInput from '../GlobalSettings/GlobalSettingInput';
import SupercarBrandRuleModal from './SupercarBrandRuleModal';

// Types matching backend entities
interface VehicleClassificationRule {
  id: number;
  category: string;
  premiumMinValue: number | null;
  premiumMinHorsePower: number | null;
  highMileageThreshold: number;
  olderModelYears: number;
  alwaysPremium: boolean;
}

interface SupercarBrandRule {
  id: number;
  kind: VehicleBrandRuleKind;
  brand: string;
  modelPattern: string | null;
  allModels: boolean;
  fuelType: string | null;
  excludeFuelType: boolean;
  bodyType: string | null;
  minMileage: number | null;
  maxMileage: number | null;
  minValue: number | null;
  maxValue: number | null;
  minYear: number | null;
  maxYear: number | null;
}

interface VehicleSampleModel {
  caseID: number;
  registrationNumber: string | null;
  vehicleBrand: string | null;
  vehicleModel: string | null;
  vehicleYearModel: string | null;
  vehicleFuel: string | null;
  vehicleBody: string | null;
  vehicleValuation: number | null;
  vehicleHorsePower: number | null;
  vehicleMileage: number;
  isPremiumCar: boolean | null;
  isElectric: boolean | null;
  isSupercar: boolean | null;
  isTransportVehicle: boolean | null;
  isHighMileage: boolean | null;
  isOlderModel: boolean | null;
  isRiskVehicle: boolean | null;
  isLowRangeElectric: boolean | null;
}

const Wrapper = styled.div`
  padding: 20px;
`;

const Section = styled.div`
  margin-bottom: 40px;
`;

const SectionTitle = styled.h2`
  font-size: 1.2em;
  margin-bottom: 10px;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 50px;
  padding: 10px 0;
  gap: 10px;

  button {
    padding: 0 10px;
    height: 100%;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 10px;
  :hover {
    cursor: pointer;
  }
`;

const EditableCell = styled.input`
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 0.9em;
  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const CheckboxCell = styled.input`
  cursor: pointer;
`;

const ReclassifyInfo = styled.span`
  font-size: 0.9em;
  color: #666;
  margin-left: 10px;
`;

const EmptySampleNote = styled.div`
  font-size: 0.9em;
  color: #888;
  font-style: italic;
  padding: 8px 0;
`;

const MessageGrid = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 12px 16px;
  align-items: center;
  max-width: 800px;

  input {
    width: 100%;
  }
`;

const MessageLabel = styled.label`
  font-weight: 500;
  color: #333;
`;

const MessageNote = styled.div`
  font-size: 0.85em;
  color: #666;
  margin-bottom: 12px;
`;

const MessageInputColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SectionIntro = styled.div`
  font-size: 0.85em;
  color: #666;
  max-width: 800px;
`;

const MessageHint = styled.small`
  color: #888;
  font-style: italic;
  font-size: 0.8em;
`;

const VEHICLE_WARNING_MESSAGE_SETTINGS: {
  setting: GlobalSettingEnum;
  label: string;
  defaultText: string;
  hint?: string;
}[] = [
  {
    setting: GlobalSettingEnum.VehicleWarningMessage_Supercar,
    label: 'Superbil',
    defaultText: 'SUPERBIL!! KRÄVER SPECIALTESTARE & SUPER-OBD',
  },
  {
    setting: GlobalSettingEnum.VehicleWarningMessage_Premium,
    label: 'Premiumbil',
    defaultText:
      'Premiumbil – noggrant test krävs. Se intranätet för info och testhjälp.',
    hint: 'OBS: denna text överskrids av ev. Superbil-text när bilen är både Superbil och Premium.',
  },
  {
    setting: GlobalSettingEnum.VehicleWarningMessage_TransportVehicle,
    label: 'Transportbil',
    defaultText:
      'Transportbil – noggrant test krävs. Se intranätet för info och testhjälp.',
  },
  {
    setting: GlobalSettingEnum.VehicleWarningMessage_Electric,
    label: 'Elbil',
    defaultText:
      'Elbil – noggrant test krävs. Se intranätet för info och testhjälp.',
  },
  {
    setting: GlobalSettingEnum.VehicleWarningMessage_LowRangeElectric,
    label: 'Elbil med dålig räckvidd',
    defaultText:
      'Elbil med dålig räckvidd – planera laddning innan avfärd. Se intranätet för info.',
    hint: 'Visas i stället för Elbil-texten när bilen har dålig räckvidd.',
  },
  {
    setting: GlobalSettingEnum.VehicleWarningMessage_HighMileage,
    label: 'Högmilad',
    defaultText:
      'Högmilad – noggrant test krävs. Se intranätet för info och testhjälp.',
  },
];

const categoryDisplayNames: Record<string, string> = {
  Elbil: 'Elbil',
  ElbilDaligRackvidd: 'Elbil med dålig räckvidd',
  Transportbil: 'Transportbil',
  ICEHybrid: 'ICE/Hybridbilar',
  Superbil: 'Superbilar',
};

const flagLabel = (val: boolean | null) => (val ? 'Ja' : '-');

const VehicleClassificationPage = () => {
  const modalStack = useModalStack();
  const [rules, setRules] = useState<VehicleClassificationRule[]>([]);
  const [supercarRules, setSupercarRules] = useState<SupercarBrandRule[]>([]);
  const [editingRule, setEditingRule] =
    useState<VehicleClassificationRule | null>(null);
  const [reclassifyCount, setReclassifyCount] = useState<number | null>(null);
  const [isReclassifying, setIsReclassifying] = useState(false);
  const [samples, setSamples] = useState<VehicleSampleModel[]>(
    []
  );

  // GlobalSettings hooks for editable warning messages
  const queryClient = useQueryClient();
  const globalSettingsResponse = useGlobalSettingsGetSettings();
  const updateGlobalSettingsCall = useGlobalSettingsUpdateSetting();
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const { watch: watchMsg, reset: resetMsg, setValue: setMsgValue } =
    useForm<{ [key: string]: string }>();

  useEffect(() => {
    if (globalSettingsResponse.data) {
      resetMsg(globalSettingsResponse.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalSettingsResponse.data]);

  const handleMsgChangeDebounced = async (
    setting: GlobalSettingEnum,
    value: string
  ) => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(async () => {
      await updateGlobalSettingsCall.mutateAsync({
        data: { key: setting, value: value ?? '' },
      });
      await queryClient.invalidateQueries([
        `${getGlobalSettingsGetSettingsQueryKey()}`,
      ]);
      globalSettingsResponse.refetch();
    }, 300);
  };

  const fetchRules = useCallback(async () => {
    try {
      const { data } = await AXIOS_INSTANCE.get<VehicleClassificationRule[]>(
        '/api/VehicleClassification/GetRules'
      );
      setRules(data);
    } catch (e) {
      // silently ignore
    }
  }, []);

  const fetchSupercarRules = useCallback(async () => {
    try {
      const { data } = await AXIOS_INSTANCE.get<SupercarBrandRule[]>(
        '/api/VehicleClassification/GetSupercarRules'
      );
      setSupercarRules(data);
    } catch (e) {
      // silently ignore
    }
  }, []);

  const fetchSamples = useCallback(async () => {
    try {
      const { data } = await AXIOS_INSTANCE.get<VehicleSampleModel[]>(
        '/api/VehicleClassification/GetSampleCases',
        { params: { perCategory: 20 } }
      );
      setSamples(data);
    } catch (e) {
      // silently ignore
    }
  }, []);

  useEffect(() => {
    fetchRules();
    fetchSupercarRules();
    fetchSamples();
  }, [fetchRules, fetchSupercarRules, fetchSamples]);

  const handleUpdateRule = async (rule: VehicleClassificationRule) => {
    try {
      await AXIOS_INSTANCE.post('/api/VehicleClassification/UpdateRule', rule);
      setEditingRule(null);
      fetchRules();
    } catch (e) {
      // silently ignore
    }
  };

  const handleDeleteSupercarRule = async (id: number) => {
    try {
      await AXIOS_INSTANCE.delete(
        `/api/VehicleClassification/DeleteSupercarRule?id=${id}`
      );
      fetchSupercarRules();
    } catch (e) {
      // silently ignore
    }
  };

  const handleReclassify = async () => {
    setIsReclassifying(true);
    try {
      const { data } = await AXIOS_INSTANCE.post<number>(
        '/api/VehicleClassification/ReclassifyOpenCases'
      );
      setReclassifyCount(data);
      fetchSamples();
    } catch (e) {
      // silently ignore
    } finally {
      setIsReclassifying(false);
    }
  };

  const handleConfirmDelete = (id: number) => {
    modalStack.push(
      <Modal
        buttons={[
          {
            label: 'Ta bort',
            onClick: () => {
              handleDeleteSupercarRule(id);
              modalStack.pop();
            },
          },
          {
            label: 'Avbryt',
            onClick: () => modalStack.pop(),
          },
        ]}
        title="Ta bort regel"
      >
        <div>Vill du ta bort denna regel?</div>
      </Modal>
    );
  };

  const handleConfirmReclassify = () => {
    modalStack.push(
      <Modal
        buttons={[
          {
            label: 'Omklassificera',
            onClick: () => {
              handleReclassify();
              modalStack.pop();
            },
          },
          {
            label: 'Avbryt',
            onClick: () => modalStack.pop(),
          },
        ]}
        title="Omklassificera alla ärenden"
      >
        <div>
          Detta kommer att omklassificera alla öppna ärenden baserat på de
          aktuella reglerna. Vill du fortsätta?
        </div>
      </Modal>
    );
  };

  // Classification rules table columns
  const ruleColumns: ColumnSetting<VehicleClassificationRule, object>[] = [
    {
      head: 'Kategori',
      render: (r) => categoryDisplayNames[r.category] || r.category,
      width: 15,
    },
    {
      head: 'Premium min värde (SEK)',
      render: (r) => {
        if (editingRule && editingRule.id === r.id) {
          const er = editingRule;
          return (
            <EditableCell
              type="number"
              value={er.premiumMinValue ?? ''}
              onChange={(e) =>
                setEditingRule({
                  ...er,
                  premiumMinValue: e.target.value
                    ? parseInt(e.target.value, 10)
                    : null,
                })
              }
            />
          );
        }
        return r.premiumMinValue?.toLocaleString('sv-SE') ?? '-';
      },
      width: 15,
    },
    {
      head: 'Premium min HK',
      render: (r) => {
        if (editingRule && editingRule.id === r.id) {
          const er = editingRule;
          return (
            <EditableCell
              type="number"
              value={er.premiumMinHorsePower ?? ''}
              onChange={(e) =>
                setEditingRule({
                  ...er,
                  premiumMinHorsePower: e.target.value
                    ? parseInt(e.target.value, 10)
                    : null,
                })
              }
            />
          );
        }
        return r.premiumMinHorsePower?.toString() ?? '-';
      },
      width: 12,
    },
    {
      head: 'Högmilad (mil)',
      render: (r) => {
        if (editingRule && editingRule.id === r.id) {
          const er = editingRule;
          return (
            <EditableCell
              type="number"
              value={er.highMileageThreshold}
              onChange={(e) =>
                setEditingRule({
                  ...er,
                  highMileageThreshold: parseInt(e.target.value, 10) || 0,
                })
              }
            />
          );
        }
        return r.highMileageThreshold.toLocaleString('sv-SE');
      },
      width: 12,
    },
    {
      head: 'Äldre modell (år)',
      render: (r) => {
        if (editingRule && editingRule.id === r.id) {
          const er = editingRule;
          return (
            <EditableCell
              type="number"
              value={er.olderModelYears}
              onChange={(e) =>
                setEditingRule({
                  ...er,
                  olderModelYears: parseInt(e.target.value, 10) || 0,
                })
              }
            />
          );
        }
        return r.olderModelYears.toString();
      },
      width: 12,
    },
    {
      head: 'Alltid premium',
      render: (r) => {
        if (editingRule && editingRule.id === r.id) {
          const er = editingRule;
          return (
            <CheckboxCell
              type="checkbox"
              checked={er.alwaysPremium}
              onChange={(e) =>
                setEditingRule({
                  ...er,
                  alwaysPremium: e.target.checked,
                })
              }
            />
          );
        }
        return r.alwaysPremium ? 'Ja' : 'Nej';
      },
      width: 10,
    },
    {
      head: '',
      render: (r) => {
        if (editingRule && editingRule.id === r.id) {
          const er = editingRule;
          return (
            <IconWrapper>
              <Button onClick={() => handleUpdateRule(er)}>Spara</Button>
              <Button onClick={() => setEditingRule(null)}>Avbryt</Button>
            </IconWrapper>
          );
        }
        return (
          <IconWrapper>
            <FontAwesomeIcon
              icon={faPen}
              onClick={(e) => {
                e.stopPropagation();
                setEditingRule({ ...r });
              }}
            />
          </IconWrapper>
        );
      },
      width: 14,
    },
  ];

  // Sample-cases table columns (one table per category)
  const sampleColumns: ColumnSetting<VehicleSampleModel, object>[] = [
    {
      head: '',
      render: (r) => <CaseOpenLink caseID={r.caseID} />,
      width: 4,
    },
    {
      head: 'Regnr',
      render: (r) => r.registrationNumber ?? '-',
      width: 12,
    },
    {
      head: 'Märke',
      render: (r) => r.vehicleBrand ?? '-',
      width: 14,
    },
    {
      head: 'Modell',
      render: (r) => r.vehicleModel ?? '-',
      width: 18,
    },
    {
      head: 'Årsmodell',
      render: (r) => r.vehicleYearModel ?? '-',
      width: 9,
    },
    {
      head: 'Drivmedel',
      render: (r) => r.vehicleFuel ?? '-',
      width: 10,
    },
    {
      head: 'Kaross',
      render: (r) => r.vehicleBody ?? '-',
      width: 12,
    },
    {
      head: 'HK',
      render: (r) => r.vehicleHorsePower?.toString() ?? '-',
      width: 6,
    },
    {
      head: 'Värdering',
      render: (r) => r.vehicleValuation?.toLocaleString('sv-SE') ?? '-',
      width: 11,
    },
    {
      head: 'Mil',
      render: (r) => r.vehicleMileage.toLocaleString('sv-SE'),
      width: 9,
    },
    {
      head: 'Elbil',
      render: (r) => flagLabel(r.isElectric),
      width: 5,
    },
    {
      head: 'Super',
      render: (r) => flagLabel(r.isSupercar),
      width: 5,
    },
    {
      head: 'Transp.',
      render: (r) => flagLabel(r.isTransportVehicle),
      width: 5,
    },
    {
      head: 'Prem.',
      render: (r) => flagLabel(r.isPremiumCar),
      width: 5,
    },
    {
      head: 'Hög mil',
      render: (r) => flagLabel(r.isHighMileage),
      width: 5,
    },
    {
      head: 'Äldre',
      render: (r) => flagLabel(r.isOlderModel),
      width: 5,
    },
    {
      head: 'Risk',
      render: (r) => flagLabel(r.isRiskVehicle),
      width: 5,
    },
    {
      head: 'Räckv.',
      render: (r) => flagLabel(r.isLowRangeElectric),
      width: 5,
    },
  ];

  const formatFilter = (r: SupercarBrandRule): string => {
    const parts: string[] = [];
    if (r.fuelType) parts.push(`${r.excludeFuelType ? 'Ej ' : ''}${r.fuelType}`);
    if (r.bodyType) parts.push(`Kaross: ${r.bodyType}`);
    if (r.minMileage != null || r.maxMileage != null) {
      const min = r.minMileage?.toLocaleString('sv-SE') ?? '0';
      const max = r.maxMileage?.toLocaleString('sv-SE') ?? '∞';
      parts.push(`${min}-${max} mil`);
    }
    if (r.minValue != null || r.maxValue != null) {
      const min = r.minValue?.toLocaleString('sv-SE') ?? '0';
      const max = r.maxValue?.toLocaleString('sv-SE') ?? '∞';
      parts.push(`${min}-${max} kr`);
    }
    if (r.minYear != null || r.maxYear != null) {
      const min = r.minYear?.toString() ?? '-';
      const max = r.maxYear?.toString() ?? '-';
      parts.push(`Årsmodell ${min}-${max}`);
    }
    return parts.length > 0 ? parts.join(', ') : '-';
  };

  // Supercar brand rules table columns
  const supercarColumns: ColumnSetting<SupercarBrandRule, object>[] = [
    {
      head: 'Bilmärke',
      render: (r) => r.brand,
      width: 14,
      sortFunction: (a, b) => a.brand.localeCompare(b.brand),
    },
    {
      head: 'Modell',
      render: (r) =>
        r.allModels ? '(Alla modeller)' : (r.modelPattern ?? '-'),
      width: 14,
    },
    {
      head: 'Filter',
      render: (r) => formatFilter(r),
      width: 30,
    },
    {
      head: '',
      render: (r) => (
        <IconWrapper>
          <FontAwesomeIcon
            icon={faPen}
            onClick={(e) => {
              e.stopPropagation();
              modalStack.push(
                <SupercarBrandRuleModal
                  rule={r}
                  onCancel={() => modalStack.pop()}
                  onSaved={() => {
                    modalStack.pop();
                    fetchSupercarRules();
                  }}
                />
              );
            }}
          />
          <FontAwesomeIcon
            icon={faTrash}
            onClick={(e) => {
              e.stopPropagation();
              handleConfirmDelete(r.id);
            }}
          />
        </IconWrapper>
      ),
      width: 8,
    },
  ];

  const renderBrandRuleSection = (
    kind: VehicleBrandRuleKind,
    title: string,
    intro?: string
  ) => (
    <Section>
      <SectionTitle>{title}</SectionTitle>
      {intro && <SectionIntro>{intro}</SectionIntro>}
      <Actions>
        <Button
          onClick={() =>
            modalStack.push(
              <SupercarBrandRuleModal
                kind={kind}
                onCancel={() => modalStack.pop()}
                onSaved={() => {
                  modalStack.pop();
                  fetchSupercarRules();
                }}
              />
            )
          }
        >
          + Ny regel
        </Button>
      </Actions>
      <Table
        columnSettings={supercarColumns}
        rows={supercarRules.filter((r) => (r.kind ?? 0) === kind)}
        useColumnWidthAsFlexAmount
      />
    </Section>
  );

  return (
    <Wrapper>
      <Section>
        <SectionTitle>Klassificeringsregler per fordonskategori</SectionTitle>
        <Table
          columnSettings={ruleColumns}
          rows={rules}
          useColumnWidthAsFlexAmount
        />
      </Section>

      {renderBrandRuleSection(
        VehicleBrandRuleKind.Supercar,
        'Superbilar - Märkes- och modellregler'
      )}

      {renderBrandRuleSection(
        VehicleBrandRuleKind.LowRangeElectric,
        'Elbilar med dålig räckvidd - Märkes- och modellregler',
        'Matchar bara elbilar. Bilen klassas efter raden "Elbil med dålig räckvidd" ovan, får en egen varningstext i ZäkraGo, och ruttplaneringen rekommenderar lastbil.'
      )}

      <Section>
        <SectionTitle>Varningstexter i Native-appen</SectionTitle>
        <MessageNote>
          Texten som visas för fälttestaren när motsvarande klassificeringsflagga
          är satt på ärendet. Lämnas raden tom används standardtexten.
        </MessageNote>
        <MessageGrid>
          {VEHICLE_WARNING_MESSAGE_SETTINGS.map((m) => (
            <React.Fragment key={m.setting}>
              <MessageLabel>{m.label}</MessageLabel>
              <MessageInputColumn>
                <GlobalSettingInput
                  setting={m.setting}
                  type="text"
                  placeholder={m.defaultText}
                  watch={watchMsg}
                  setValue={setMsgValue}
                  handleInputChangeDebounced={handleMsgChangeDebounced}
                />
                {m.hint && <MessageHint>{m.hint}</MessageHint>}
              </MessageInputColumn>
            </React.Fragment>
          ))}
        </MessageGrid>
      </Section>

      <Section>
        <SectionTitle>Omklassificering</SectionTitle>
        <Actions>
          <Button onClick={handleConfirmReclassify} disabled={isReclassifying}>
            {isReclassifying
              ? 'Omklassificerar...'
              : 'Omklassificera alla öppna ärenden'}
          </Button>
          {reclassifyCount !== null && (
            <ReclassifyInfo>
              {reclassifyCount} ärenden omklassificerade
            </ReclassifyInfo>
          )}
        </Actions>
      </Section>

      <Section>
        <SectionTitle>Stickprov — senaste öppna ärenden</SectionTitle>
        {samples.length === 0 ? (
          <EmptySampleNote>Inga klassificerade ärenden.</EmptySampleNote>
        ) : (
          <Table
            columnSettings={sampleColumns}
            rows={samples}
            useColumnWidthAsFlexAmount
          />
        )}
      </Section>
    </Wrapper>
  );
};

export default VehicleClassificationPage;
